import { LegalLayout, Clause } from "./LegalLayout";
import { Zap, Landmark, FileWarning } from "lucide-react";

export default function LegalTerms() {
  return (
    <LegalLayout eyebrow="LEGAL · TERMS OF SERVICE" title="Terms of Service" updated="2026-06-01" active="terms">
      <Clause n="1" heading="The service">
        <p>
          Checkmate Payroll provides payroll calculation, tax withholding computation, time and attendance tracking,
          HR document storage, and client-billing tooling for United States businesses. Checkmate is a software
          provider. It is not a bank, not a licensed money transmitter, and not your employer of record. You remain
          the employer, the tax filer of record, and the party legally responsible for paying your people correctly
          and on time.
        </p>
      </Clause>

      <Clause n="2" heading="Your account">
        <p>
          You are responsible for the security of your credentials and for every action taken under your login.
          Sharing an administrator login is a breach of these terms. Multi-factor authentication is available and
          strongly recommended for any account with payout authority.
        </p>
      </Clause>

      <Clause n="3" heading="Dual-Fuel funding — your responsibility">
        <div className="border-2 border-ink p-4 space-y-3" data-testid="terms-dual-fuel">
          <div className="flex gap-3">
            <Zap className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <b>Instant rail — business debit card.</b> When you arm a business debit card, eligible employees are paid
              by instant payout. You warrant that you are an authorized signer on that card, that sufficient funds are
              available at the moment of the run, and that the card supports instant payouts. Instant delivery carries a
              processor convenience fee, currently 1.5% with a $0.50 minimum per payout, which is <b>absorbed by your
              business</b> and itemized on the sign-off screen before you authorize the batch. Employee net pay is never
              reduced by this fee. Once an instant payout is accepted by the card network it is irreversible.
            </div>
          </div>
          <div className="flex gap-3">
            <Landmark className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <b>Standard rail — linked bank account (ACH).</b> When you fund by ACH you are responsible for maintaining a
              sufficient balance for the full net pay plus any employer taxes for the entire clearing window. You authorize
              Checkmate and its processors to debit the linked account for the amounts shown on the authorized run.
            </div>
          </div>
          <div className="leading-relaxed">
            <b>In both cases funding is your obligation, not ours.</b> Checkmate does not advance, float, guarantee or
            underwrite payroll. Insufficient funds, a declined card, a closed account, an expired authorization, a bank
            hold or a processor risk review may cause a payout to fail or reverse. Returned-item fees, overdraft charges,
            late-payment penalties and any wage-and-hour exposure arising from a failed funding instruction are yours.
            You agree to monitor the funding health indicator and the coverage warning shown before sign-off, and not to
            authorize a run you cannot fund.
          </div>
        </div>
      </Clause>

      <Clause n="4" heading="ACH clearing timelines">
        <p>
          ACH is a batch network with published settlement windows, not a real-time system. Standard transfers are
          initiated on the next banking day after authorization and typically settle in approximately{" "}
          <b>48 hours (two banking days)</b>, and may take longer across weekends, federal banking holidays, or when a
          receiving institution places a hold. Timelines shown in the application — including the countdown in the
          Funding Health indicator and the availability timestamps in the Cash Flow Drawer — are good-faith estimates
          based on network norms, not guarantees.
        </p>
        <p>
          You are responsible for authorizing payroll early enough to hit your pay date. Checkmate is not liable for a
          late paycheck caused by authorizing a run inside the clearing window, by a bank holiday, by a receiving-bank
          delay, or by a processor review. Instant payouts settle in minutes but only for eligible debit-card
          destinations; anyone without an eligible card is paid on the standard rail and is subject to the timelines
          above.
        </p>
      </Clause>

      <Clause n="5" heading="Manual check entries and off-platform payments">
        <div className="border-2 border-warn bg-warn/10 p-4 flex gap-3" data-testid="terms-manual-checks">
          <FileWarning className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="leading-relaxed">
            When you record a payment as a manual check, cash, or any other off-platform method, Checkmate performs no
            verification whatsoever. We do not confirm that a check was written, signed, delivered, negotiated, honoured
            or correctly dated. Such entries are bookkeeping records that you assert to be true. You are solely liable
            for the accuracy of manually entered payment records and for any consequence of an inaccurate one, including
            duplicate payment, non-payment, stop-payment costs, check fraud, escheatment obligations, misfiled tax
            returns, and wage-claim exposure. Checkmate's liability for a manual check entry is expressly limited to
            correcting the record on request.
          </div>
        </div>
      </Clause>

      <Clause n="6" heading="Financial and tax accuracy">
        <p>
          Payroll math — federal and state withholding, FICA, local taxes, benefits and garnishments — is computed
          deterministically from the data you enter and from published rate tables. Rate tables are maintained on a
          best-effort basis and some future-year figures are provisional estimates, labelled as such in the product. You
          are the tax filer of record and must review pay stubs, filings and rate assumptions with a qualified CPA.
          Checkmate does not remit taxes on your behalf unless a separate written agreement says so.
        </p>
      </Clause>

      <Clause n="7" heading="Data retention">
        <p>
          Records are retained for seven years, then flagged for reviewed deletion, as set out in our{" "}
          <a href="/legal/retention" className="underline">Data Retention Policy</a>, which forms part of these terms.
        </p>
      </Clause>

      <Clause n="8" heading="AI disclosure">
        <p>
          Checkmate uses machine-learning models for the AI Office Manager, Billing Concierge grouping and the Buddy
          support chat. AI output is advisory only; every payroll figure and filing is computed deterministically. You
          are responsible for reviewing AI output before it reaches an employee, client or tax authority.
        </p>
      </Clause>

      <Clause n="9" heading="Limitation of liability">
        <p>
          To the maximum extent permitted by law, Checkmate is not liable for indirect, incidental, special,
          consequential, exemplary or punitive damages, nor for lost profits, lost goodwill, or missed payroll deadlines
          arising from user error, incorrect or incomplete data entry, insufficient funding, manual check entries, or a
          third-party outage or decision at Stripe, Plaid, Twilio, Resend or any financial institution. Our maximum
          aggregate liability for all claims is limited to the subscription fees you paid in the twelve months preceding
          the claim. Nothing here limits liability that cannot lawfully be limited.
        </p>
      </Clause>

      <Clause n="10" heading="Termination">
        <p>
          You may close your account at any time from Settings → Right to be Forgotten. We may suspend an account that
          breaches these terms or that is used to process fraudulent payments. Suspension is not a refund. Records we
          are legally required to retain survive termination for the retention period.
        </p>
      </Clause>

      <Clause n="11" heading="DMCA">
        <p>
          Copyright notices go to <b>legal@checkmatepayrolls.com</b> with your signature as rights-holder or agent, the
          asset path, a description of the work, your contact details, a good-faith statement that the use is
          unauthorized, and a statement under penalty of perjury that the notice is accurate. We act promptly under 17
          U.S.C. §512(c) and handle counter-notices under §512(g).
        </p>
      </Clause>

      <Clause n="12" heading="Governing law">
        <p>These terms are governed by the laws of the State of Delaware, USA, without regard to conflict-of-laws rules.</p>
      </Clause>
    </LegalLayout>
  );
}
